import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import AnimatedText from './AnimatedText';

const services = [
  {
    number: '01',
    title: 'Short-Form Production',
    desc: 'Reels, TikToks, Shorts engineered for the first three seconds.',
    stat: '200+',
    statLabel: 'Clips produced',
    image: 'https://images.unsplash.com/photo-1598550476439-6847785fcea6?w=1200&auto=format&fit=crop&q=75',
    // asymmetric grid spans: col / row
    span: 'lg:col-span-7',
    aspect: 'aspect-[16/12]',
  },
  {
    number: '02',
    title: 'Creator-Led UGC',
    desc: 'Authentic content shot by real creators — including Rutvi herself.',
    stat: '3×',
    statLabel: 'Higher CTR',
    image: 'https://images.unsplash.com/photo-1496440737103-cd596325d314?w=1200&auto=format&fit=crop&q=75',
    span: 'lg:col-span-5',
    aspect: 'aspect-[4/5]',
    accent: true, // dark card
  },
  {
    number: '03',
    title: 'Social Media Management',
    desc: 'Planning, copywriting, scheduling, community — done for you.',
    stat: 'Weekly',
    statLabel: 'Strategy + drops',
    image: 'https://images.unsplash.com/photo-1611605645802-c21be743c321?w=1200&auto=format&fit=crop&q=75',
    span: 'lg:col-span-5',
    aspect: 'aspect-[4/5]',
  },
  {
    number: '04',
    title: 'Online Presence & Brand',
    desc: 'Positioning, identity, landing page — one coherent brand.',
    stat: '10s',
    statLabel: 'To earn trust',
    image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=1200&auto=format&fit=crop&q=75',
    span: 'lg:col-span-7',
    aspect: 'aspect-[16/12]',
    accent: true,
  },
];

const ServiceCard = ({ s, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  // parallax image movement
  const imgY = useTransform(scrollYProgress, [0, 1], ['-12%', '12%']);
  const imgScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.15, 1.02, 1.15]);

  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const dark = s.accent;

  return (
    <motion.article
      ref={ref}
      data-testid={`service-card-${s.number}`}
      data-cursor-label="View"
      onClick={scrollToContact}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.7, delay: index * 0.08, ease: [0.2, 0.65, 0.3, 0.9] }}
      whileHover={{ y: -8 }}
      className={`${s.span} ${s.aspect} relative overflow-hidden rounded-[2rem] cursor-pointer group ${
        dark ? 'bg-[#1C1917]' : 'bg-[#E7E5E4]'
      }`}
    >
      {/* Image */}
      <motion.img
        src={s.image}
        alt={s.title}
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.18] group-hover:saturate-150"
        loading="lazy"
      />

      {/* Dark gradient readability overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/85 via-[#1C1917]/30 to-[#1C1917]/0" />

      {/* Orange tint on hover */}
      <div className="absolute inset-0 bg-[#AA94FF]/0 group-hover:bg-[#AA94FF]/10 transition-colors duration-500" />

      {/* Top row: service badge + arrow */}
      <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
        <span className="bg-[#F4F4F3]/90 backdrop-blur-md text-[#1C1917] text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-full">
          Service / {s.number}
        </span>
        <motion.div
          whileHover={{ rotate: 45, scale: 1.12 }}
          transition={{ type: 'spring', damping: 14, stiffness: 220 }}
          className="w-12 h-12 rounded-full bg-[#F4F4F3] group-hover:bg-[#AA94FF] flex items-center justify-center transition-colors"
        >
          <ArrowUpRight className="w-5 h-5 text-[#1C1917] group-hover:text-[#F4F4F3] transition-colors" />
        </motion.div>
      </div>

      {/* Bottom content */}
      <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-10">
        {/* Animated divider */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 + index * 0.08 }}
          className="h-[1.5px] bg-[#F4F4F3]/30 origin-left mb-6"
        />

        <div className="flex items-end justify-between gap-8">
          {/* Title + description */}
          <div className="flex-1 min-w-0">
            <AnimatedText
              as="h3"
              className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-[-0.02em] leading-[1] text-[#F4F4F3] mb-3"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              y={30}
              stagger={0.04}
            >
              {s.title}
            </AnimatedText>
            <p className="text-sm text-[#F4F4F3]/70 leading-relaxed max-w-md hidden sm:block">
              {s.desc}
            </p>
          </div>

          {/* Stat */}
          <div className="shrink-0 text-right">
            <p
              className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#AA94FF] tracking-[-0.04em] leading-none"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {s.stat}
            </p>
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#F4F4F3]/60 mt-2 font-semibold">
              {s.statLabel}
            </p>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

const Services = () => {
  const headerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: headerRef,
    offset: ['start end', 'end start'],
  });
  const wordmarkX = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-28 sm:py-40 px-6 sm:px-8 lg:px-12 bg-[#F4F4F3] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div ref={headerRef} className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20 sm:mb-24">
          <p className="lg:col-span-4 text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#AA94FF]">
            Featured services
          </p>
          <div className="lg:col-span-8">
            <AnimatedText
              as="h2"
              className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-[-0.03em] leading-[0.98] text-[#1C1917]"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              y={60}
              stagger={0.06}
            >
              What we do best —
            </AnimatedText>
            <motion.span
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="block text-4xl sm:text-6xl lg:text-7xl italic font-light tracking-[-0.02em] leading-[0.98] text-[#AA94FF] mt-2"
              style={{ fontFamily: "'Cabinet Grotesk', serif" }}
            >
              content that performs.
            </motion.span>
          </div>
        </div>

        {/* Asymmetric service card grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">
          {services.map((s, i) => (
            <ServiceCard key={s.number} s={s} index={i} />
          ))}
        </div>

        {/* Decorative parallax wordmark */}
        <motion.div
          style={{ x: wordmarkX }}
          aria-hidden="true"
          className="mt-20 sm:mt-28 select-none pointer-events-none"
        >
          <p
            className="text-[18vw] lg:text-[14rem] font-black tracking-[-0.06em] leading-none text-[#1C1917]/[0.04] whitespace-nowrap"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            HYPELAB · HYPELAB · HYPELAB
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
